import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Text } from '@paygreen/paygreen-ui';
import { useMode } from '../../../context';
import { postCsvCatalog } from '../service';
import AddCsvProductForm from './AddCsvProductForm';
import ProductModal from '../shared/ProductModal';

const AddCsvProduct = ({
    handleRefreshData,
    setOpen,
    isOpen,
    t,
    setIsLoading,
    isLoading,
}) => {
    const { mode } = useMode();

    const [errorMessage, setErrorMessage] = useState(null);
    const [tempFile, setTempFile] = useState();

    // initializing all react-hook-form values and methods
    const defaultValues = {
        inputCsv: '',
    };

    const { register, handleSubmit, reset, errors, setError } = useForm();

    const cancelCloseModal = useCallback(() => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            setErrorMessage();
            setTempFile();
            reset();
        }, 500);
    }, [setOpen, reset]);

    const fileName = tempFile?.name || '';

    /**
     * Post request to API to create a new product
     * @param {object} newProduct - object with info to register new product to Climatekit database
     */
    const addCsvCatalog = useCallback(
        (data) => {
            setIsLoading(true);

            postCsvCatalog(data, mode)
                .then(() => {
                    handleRefreshData(true);

                    cancelCloseModal();

                    !isLoading &&
                        toast(
                            <Text colorPallet="theme" textSize="xs">
                                <Trans i18nKey="form.toastSuccess.addCsvProduct">
                                    ...
                                    <Text
                                        htmlTag="strong"
                                        colorPallet="theme"
                                        textSize="xs"
                                    >
                                        {{
                                            catalog: fileName,
                                        }}
                                    </Text>
                                    ...
                                </Trans>
                            </Text>,
                        );
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;
    
                    setErrorMessage(status + ' / ' + detail);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [
            handleRefreshData,
            setIsLoading,
            cancelCloseModal,
            isLoading,
            fileName,
            mode,
        ],
    );

    const submitForm = () => {
        setErrorMessage(null);

        let formData = new FormData();
        formData.append('inputCsv', tempFile);

        addCsvCatalog(formData);
    };

    return (
        <ProductModal
            modalTitle={t('matching.addCsvForm.modalTitle')}
            blockWidth="xl"
            isOpen={isOpen}
            isLoading={isLoading}
            cancelCloseModal={cancelCloseModal}
            errorMessage={errorMessage}
            hasDisabledMode={!tempFile}
        >
            <AddCsvProductForm
                errors={errors}
                register={register}
                reset={reset}
                onSubmit={handleSubmit(submitForm)}
                defaultValues={defaultValues}
                tempFile={tempFile}
                setTempFile={setTempFile}
                setErrorMessage={setErrorMessage}
                errorMessage={errorMessage}
                setError={setError}
            />
        </ProductModal>
    );
};

AddCsvProduct.propTypes = {
    handleRefreshData: PropTypes.func,
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    setIsLoading: PropTypes.func,
};

AddCsvProduct.defaultProps = {
    isOpen: false,
};

export default withTranslation('common')(AddCsvProduct);
